import { Button, Card, Col, Empty, Row } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { getToken } from "../../utils/auth";
import DynamicForm from "../../common/DynamicForm";
import { getUserInfo } from "../../utils/utils";
import MarkdownRenderer from "../../common/MarkdownRenderer";
import { useEffect } from "react";
import success from "../../assets/svg/icons/success.svg";
import { ReactComponent as MailOpen } from "../../assets/svg/icons/mail-open.svg";
import { ReactComponent as Call } from "../../assets/svg/icons/call.svg";
import profile from "../../assets/svg/lead-profile.svg";
import noAction from "../../assets/svg/icons/no-action.svg";
import emailSent from "../../assets/svg/icons/email-send.svg";
import { useNavigate } from "react-router";
import DualEmailDraftPanel from "./DualEmailDraftPanel";

const RightPanel = ({
  sendInputToSession,
  sessionId,
  chatConversation,
  handleConversation,
  setShowSidePanel,
  sendQuery,
  showSidePanel,
}) => {
  const navigate = useNavigate();
  const { id, account } = getUserInfo();

  const handleSendInput = (data) => {
    sendInputToSession(
      {
        sessionId,
        userId: id,
        accountId: account?.id,
        ...data,
      },
      getToken(),
    );
  };

  const normalizeRequiredFields = (requiredFields) =>
    Object.fromEntries(Object.entries(requiredFields || {}));

  const lastMessage = chatConversation?.[chatConversation.length - 1];

  const isInputRequired = lastMessage?.stage === "INPUT_REQUIRED";

  const isSelectLead =
    (isInputRequired &&
      lastMessage?.type === "CLIENT_LEAD.UPDATE.SELECT_LEAD") ||
    (isInputRequired &&
      lastMessage?.type === "EMAIL.INITIATE_DRAFT.SELECT_LEAD");

  const isFormRequired =
    isInputRequired &&
    [
      "CLIENT_LEAD.CREATE.FORM",
      "CLIENT_LEAD.UPDATE.FIELDS",
      "CLIENT_LEAD.PUT.VALIDATION",
      "CLIENT_LEAD.POST.VALIDATION",
      "EMAIL.INITIATE_DRAFT.SHOW_INTENT_PANEL",
      "EMAIL.INITIATE_DRAFT.SHOW_INTENT_PANEL",
      "EMAIL.INITIATE_DRAFT.SHOW_INTENT_PANEL",
      "EMAIL.INITIATE_DRAFT.SHOW_INTENT_PANEL",
    ].includes(lastMessage?.type);

  const isSidePanel = ["SIDE_PANEL"].includes(lastMessage?.targetArea);
  const isSuccess = ["SUCCESS"].includes(lastMessage?.stage);
  const hasMarkdownTable = (text = "") => {
    return /\|(.+)\|\n\|([-:\s|]+)\|/.test(text);
  };
  const isEmailIntent =
    lastMessage?.type === "EMAIL.INITIATE_DRAFT" && isSidePanel;

  const isTable = hasMarkdownTable(lastMessage?.text);
  const cardProps =
    isSelectLead || isFormRequired || isSidePanel || isSuccess
      ? {
          title: (
            <>
              {isSuccess ? (
                <div className="chat-message">
                  <MarkdownRenderer
                    content={
                      chatConversation[chatConversation?.length - 1].message
                    }
                    sendQuery={sendQuery}
                  />
                </div>
              ) : (
                <div>{lastMessage?.message} </div>
              )}
            </>
          ),
          className: "chat-widget right-widget p-0",
        }
      : !isSelectLead &&
          !isFormRequired &&
          !isSidePanel &&
          !isTable &&
          !isSuccess
        ? { className: "chat-no-body right-widget" }
        : {
            className: "chat-widget-without-title right-widget p-0",
          };

  function extractLeadDetails(text) {
    const result = {};
    const regex = /-\s\*\*(.+?)\*\*:\s(.+)/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const key = match[1].toLowerCase().replace(/\s+/g, "_");
      const value = match[2].trim();
      result[key] = value;
    }

    return result;
  }

  const jsonData =
    isSuccess &&
    extractLeadDetails(chatConversation[chatConversation?.length - 2].text);

  const isUpdateSuccess = lastMessage?.type === "CLIENT.UPDATE.SUCCESS";
  const isEmailSuccess = lastMessage?.type === "EMAIL_SUCCESS";
  useEffect(() => {
    if (!showSidePanel) {
      setShowSidePanel &&
        setShowSidePanel(
          isSelectLead || isFormRequired || isSidePanel || isTable || isSuccess,
        );
    }
    if (!chatConversation?.length) {
      setShowSidePanel(false);
    }
  }, [isSelectLead, isFormRequired, isSidePanel, isTable, isSuccess]);

  return (
    <Card
      {...cardProps}
      title={
        <div className="tw-flex tw-items-center tw-justify-between">
          Action Workspace{" "}
          <CloseOutlined onClick={() => setShowSidePanel(false)} />
        </div>
      }
    >
      {/* DEFAULT EMPTY STATE */}
      {!isSelectLead &&
        !isFormRequired &&
        !isSidePanel &&
        !isTable &&
        !isSuccess && (
          <div className="tw-flex tw-items-center tw-justify-center tw-flex-col tw-h-full">
            <Empty
              image={noAction}
              description={
                <>
                  <div className="tw-text-base tw-font-bold tw-text-text-primary">
                    No action required
                  </div>
                  <div className="tw-text-sub-text tw-text-sm">
                    Keep chatting with the AI actions and forms will appear here
                    when needed.
                  </div>
                </>
              }
            />
          </div>
        )}

      {isEmailIntent && (
        <>
          <DualEmailDraftPanel recipient={lastMessage?.payload?.lead} />
        </>
      )}

      {isSuccess && (
        <>
          {isEmailSuccess ? (
            <div className="tw-py-8 tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
              <div className="tw-w-[50%] tw-text-text-primary tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
                <img src={emailSent} alt="" />
                <div className="tw-text-lg tw-font-medium">
                  Email Sent Successfully
                </div>

                <div className="tw-text-sm tw-text-sub-text tw-mb-4">
                  sent to{" "}
                  {
                    chatConversation[chatConversation?.length - 2]?.payload
                      ?.lead?.emailId
                  }
                </div>

                <Button
                  className="tw-my-3 !tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
                  onClick={() => navigate("/search-lead")}
                >
                  View Lead Details
                </Button>
              </div>
            </div>
          ) : (
            <div className="tw-py-8 tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
              <div className="tw-w-[50%] tw-text-text-primary tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
                <img src={success} alt="" />
                <div className="tw-text-lg tw-font-medium">
                  Lead {isUpdateSuccess ? "Updated" : "Created"} Successfully
                </div>
                {!isUpdateSuccess && (
                  <div className="tw-text-sm tw-text-sub-text tw-mb-4">
                    This Lead has been added and is ready for follow-up
                  </div>
                )}

                <div className="tw-text-text-primary tw-my-3 tw-border tw-border-solid tw-border-[#CDD2DF] tw-rounded-[10px] tw-shadow tw-w-full tw-p-5">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-2">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <img src={profile} alt="" className="tw-size-[30px]" />
                      <div className="tw-text-lg tw-font-medium ">
                        {jsonData?.name}
                      </div>
                    </div>
                    {!isUpdateSuccess && (
                      <div className="tw-rounded-md tw-bg-theme tw-text-white tw-text-xs tw-font-medium tw-px-2 tw-py-1">
                        New
                      </div>
                    )}
                  </div>

                  <div className="tw-px-2 tw-text-sub-text tw-mb-2 tw-flex tw-items-center tw-gap-3 tw-text-sm">
                    <MailOpen className="tw-size-[18px] tw-text-sub-text" />
                    {jsonData?.email}
                  </div>
                  <div className="tw-px-2 tw-text-sub-text tw-flex tw-items-center tw-gap-3 tw-text-sm">
                    <Call className="tw-size-[18px] tw-text-sub-text" />
                    {jsonData?.mobile}
                  </div>
                </div>

                <Button
                  block
                  className="tw-mb-3 !tw-bg-theme tw-font-medium !tw-outline-0 !tw-border-theme !tw-text-white tw-font-medium tw-text-sm"
                  onClick={() => navigate("/search-lead")}
                >
                  View Lead Details
                </Button>
                <Button
                  block
                  onClick={() =>
                    sendQuery(
                      isUpdateSuccess ? "Update lead" : "create new lead",
                    )
                  }
                >
                  {isUpdateSuccess ? "Update" : "Create"} another lead
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* SELECT LEAD OPTIONS */}
      {isSelectLead && (
        <Row gutter={[24, 24]}>
          {lastMessage?.payload?.options?.map((lead) => (
            <Col key={lead.id} xs={24} lg={12}>
              <div
                onClick={() => {
                  handleConversation(lead.emailId);
                  handleSendInput({
                    searchTerm: lead.emailId || lead.id,
                    choice: lead.emailId || lead.id,
                    id: lead.id,
                  });
                }}
                className="tw-cursor-pointer tw-text-text-primary tw-my-3 tw-border tw-border-solid tw-border-[#CDD2DF] tw-rounded-[10px] tw-shadow tw-w-full tw-p-5"
              >
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                  <img src={profile} alt="" className="tw-size-[30px]" />
                  <div className="tw-text-lg tw-font-medium ">
                    {lead?.fullname ||
                      `${lead?.firstname || ""} ${lead?.lastname || ""}`}
                  </div>
                </div>

                <div className="tw-px-2 tw-text-sub-text tw-mb-2 tw-flex tw-items-center tw-gap-3 tw-text-sm">
                  <MailOpen className="tw-size-[18px] tw-text-sub-text" />
                  {lead?.emailId}
                </div>
                <div className="tw-px-2 tw-text-sub-text tw-flex tw-items-center tw-gap-3 tw-text-sm">
                  <Call className="tw-size-[18px] tw-text-sub-text" />
                  {lead?.mobile}
                </div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {isTable && <MarkdownRenderer content={lastMessage.text} />}

      {/* DYNAMIC FORM */}
      {isFormRequired && lastMessage?.payload && (
        <DynamicForm
          title={cardProps?.title}
          payload={{
            ...lastMessage.payload,
            type: lastMessage?.type,
            requiredFields: normalizeRequiredFields(
              lastMessage.payload.requiredFields,
            ),
          }}
          initialValues={
            lastMessage?.payload?.error?.existingLead ||
            lastMessage?.payload?.template
          }
          submitLabel="Submit"
          onSubmit={handleSendInput}
        />
      )}
    </Card>
  );
};

export default RightPanel;
